
import debounce from 'lodash.debounce';
import {
  MapEvents,
  MapProperty,
  MarkerEvents,
  NativeMapListeners,
  NativeMarkerListeners,
} from '@/components/maps/enums';
import MarkersService from './servises/MarkersService';
import MapService from './servises/MapService';
import DrawingAreaService from './servises/DrawingAreaService';
import DrawingManagerService from './servises/DrawingManagerService';
import MapProps from './mixins/MapProps';

const DEBOUNCE_TIME = 500;

export default {
  name: 'GoogleMap',
  mixins: [MapProps],
  data() {
    return {
      geocodeCoords: null,
      markersService: new MarkersService(),
      mapService: new MapService(),
      drawingAreaService: new DrawingAreaService(),
      drawingManagerService: new DrawingManagerService(),
      isMapMounted: false,
      isDestroyed: false,
    };
  },

  watch: {
    [MapProperty.MarkersData]: {
      handler: 'renderMarkers',
    },
    [MapProperty.Zoom]: {
      handler: 'setZoom',
    },
    [MapProperty.Center]: {
      handler: 'setCenter',
      deep: true,
    },
    [MapProperty.Bounds]: {
      handler: 'setBounds',
    },
    [MapProperty.DrawingArea]: {
      handler: 'drawArea',
    },
    [MapProperty.DrawingOptions]: {
      handler: 'setDrawingOptions',
    },
  },

  async mounted() {
    await this.$nextTick();
    await this.initServices();
    if (this.isDestroyed) {
      this.destroyServices();
      return;
    }
    this.renderMarkers();
    this.setDrawingOptions();
    this.drawArea();
    this.addMapListeners();

    const { bounds } = this.getMapPosition();

    if (bounds) {
      this.emitMounted();
    }
  },

  beforeDestroy() {
    this.isDestroyed = true;
    this.destroyServices();
  },

  methods: {
    async initServices() {
      await this.mapService.initMap(
        this.$refs.map,
        {
          center: this.center,
          zoom: this.zoom,
          clickableIcons: false,
        },
      );
      await this.markersService.init();
      await this.drawingManagerService.init();
      await this.drawingAreaService.init();
    },

    destroyServices() {
      this.mapService.removeAllListeners();
      this.markersService.removeAllListeners();
      this.drawingManagerService.removeAllListeners();
      this.drawingAreaService.removeAllListeners();
    },

    renderMarkers() {
      this.markersService.removeAllMarkers();
      this.markersService.renderMarkers(this.markersData, this.mapService.map);
      this.addMarkersListeners();
    },

    getMapPosition() {
      return {
        boundsChangedTime: new Date().getTime(),
        bounds: this.mapService.getBounds(),
        center: this.mapService.getCenter(),
        zoom: this.mapService.getZoom(),
      };
    },

    setCenter(newCenter, oldCenter) {
      if (newCenter && (newCenter?.lat !== oldCenter?.lat || newCenter?.lon !== oldCenter?.lon)) {
        this.mapService.setCenter(newCenter);
        this.$emit(MapEvents.CenterChanged, this.getMapPosition());
      }
    },

    setZoom(newZoom) {
      if (newZoom) {
        this.mapService.setZoom(newZoom);
      }
    },

    setBounds(bounds) {
      if (bounds) {
        this.mapService.setBounds(bounds);
      }
    },

    setDrawingOptions() {
      if (this[MapProperty.DrawingOptions]) {
        this.drawingManagerService.removeAllListeners();
        this.drawingManagerService.setDrawingOptions(
          this[MapProperty.DrawingOptions],
          this.mapService.map,
        );
        this.drawingManagerService.addListener(this.ondDrawingAreaComplete);
      } else {
        this.drawingManagerService.removeDrawing();
      }
    },

    drawArea() {
      if (this[MapProperty.DrawingArea]) {
        this.drawingAreaService.draw(
          this[MapProperty.DrawingArea],
          this[MapProperty.DrawingOptions],
          this.mapService.map,
        );
        this.drawingAreaService.addListener(this.ondDrawingAreaChange);
      } else {
        this.drawingAreaService.clear();
      }
    },

    ondDrawingAreaComplete(event) {
      this.emitMapEvent(MapEvents.DrawingAreaComplete, event);
    },

    ondDrawingAreaChange(value) {
      this.emitMapEvent(MapEvents.DrawingAreaChanged, value);
    },

    emitMapEvent(eventName, value = this.getMapPosition()) {
      this.$emit(eventName, value);
    },

    emitMapDragend() {
      this.emitMapEvent(MapEvents.MapDragend);
    },

    emitMapDragstart() {
      this.emitMapEvent(MapEvents.MapDragstart);
    },

    emitTiltChanged() {
      this.emitMapEvent(MapEvents.TiltChanged);
    },

    emitZoomChanged() {
      this.emitMapEvent(MapEvents.ZoomChanged);
    },

    emitMounted() {
      this.isMapMounted = true;
      this.emitMapEvent(MapEvents.Mounted);
    },

    addMapListeners() {
      let dragendTimeout;
      const onDragend = () => {
        dragendTimeout = setTimeout(this.emitMapDragend, DEBOUNCE_TIME);
      };
      const onTiltChanged = () => {
        if (!this.isMapMounted) {
          this.emitMounted();
        }

        this.emitTiltChanged();
      };

      const onZoomChanged = debounce(
        () => this.emitZoomChanged(),
        DEBOUNCE_TIME,
      );
      const onDragStart = () => {
        clearTimeout(dragendTimeout);
        this.emitMapDragstart();
      };

      this.mapService.addMapListener(NativeMapListeners.Dragstart, onDragStart);
      this.mapService.addMapListener(NativeMapListeners.Dragend, onDragend);
      this.mapService.addMapListener(NativeMapListeners.TiltChanged, onTiltChanged);
      this.mapService.addMapListener(NativeMapListeners.ZoomChanged, onZoomChanged);
    },

    addMarkersListeners() {
      const createEmitCallback = (event) => (instance, index) => {
        this.$emit(event, { map: this.mapService.map, ...instance, index });
      };
      const emitClick = createEmitCallback(MarkerEvents.MarkerClick);
      const emitDragend = createEmitCallback(MarkerEvents.MarkerDragend);

      this.markersService.addMarkersListener(NativeMarkerListeners.Dragend, emitDragend);
      this.markersService.addMarkersListener(NativeMarkerListeners.Click, emitClick);
    },
  },
};
